import React, { useState, useEffect } from "react";
import { useEmailSections } from "../../Context/EmailSectionContext";

/**
 * H1_Desc_6 Component
 * Displays a heading (H1), description paragraphs, a coupon code block with dashed border,
 * two CTA buttons, an inspirational message section, and closing signature text
 * Centered layout with clean, simple design
 */
const H1Desc6 = (props) => {
  const { id, previewMode, styleSettings = {}, data } = props;
  const { sections, updateSection } = useEmailSections();

  const sectionData = previewMode
    ? data || props
    : sections.find((section) => section.id === id) || {};

  const {
    title = "",
    description = "",
    couponInstruction = "",
    couponCode = "",
    button1Text = "",
    button1Link = "#",
    inspirationalMessage = "",
    button2Text = "",
    button2Link = "#",
    closingText = "",
    component_name = "H1_Desc_6",
  } = sectionData;

  const [isEditing, setIsEditing] = useState(false);
  const [localTitle, setLocalTitle] = useState(title);
  const [localDescription, setLocalDescription] = useState(description);
  const [localCouponInstruction, setLocalCouponInstruction] = useState(couponInstruction);
  const [localCouponCode, setLocalCouponCode] = useState(couponCode);
  const [localButton1Text, setLocalButton1Text] = useState(button1Text);
  const [localButton1Link, setLocalButton1Link] = useState(button1Link);
  const [localInspirationalMessage, setLocalInspirationalMessage] = useState(inspirationalMessage);
  const [localButton2Text, setLocalButton2Text] = useState(button2Text);
  const [localButton2Link, setLocalButton2Link] = useState(button2Link);
  const [localClosingText, setLocalClosingText] = useState(closingText);

  useEffect(() => {
    setLocalTitle(title || "");
    setLocalDescription(description || "");
    setLocalCouponInstruction(couponInstruction || "");
    setLocalCouponCode(couponCode || "");
    setLocalButton1Text(button1Text || "");
    setLocalButton1Link(button1Link || "#");
    setLocalInspirationalMessage(inspirationalMessage || "");
    setLocalButton2Text(button2Text || "");
    setLocalButton2Link(button2Link || "#");
    setLocalClosingText(closingText || "");
  }, [id, title, description, couponInstruction, couponCode, button1Text, button1Link, inspirationalMessage, button2Text, button2Link, closingText]);

  const handleSave = () => {
    updateSection(id, {
      id,
      component_name,
      title: localTitle,
      description: localDescription,
      couponInstruction: localCouponInstruction,
      couponCode: localCouponCode,
      button1Text: localButton1Text,
      button1Link: localButton1Link,
      inspirationalMessage: localInspirationalMessage,
      button2Text: localButton2Text,
      button2Link: localButton2Link,
      closingText: localClosingText,
    });
    setIsEditing(false);
  };

  // Helper function to split description into paragraphs
  const getParagraphs = (text) => {
    if (!text) return [];
    // Split by double newlines
    const paragraphs = text.split(/\n\n+/).filter((p) => p.trim());
    if (paragraphs.length === 0 && text.trim()) {
      return text.split(/\n/).filter((p) => p.trim());
    }
    return paragraphs;
  };

  // Helper function to parse bold text (**text**)
  const parseBoldText = (text) => {
    if (!text) return text;
    return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  };

  if (isEditing && !previewMode) {
    return (
      <div className="email-component h1-desc-6-editor p-3 mb-3 border rounded bg-white">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Edit Heading & Description with Coupon Code & Two Buttons</h4>
          <button className="btn btn-sm btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Heading (H1)</label>
          <input
            className="form-control"
            value={localTitle}
            onChange={(e) => setLocalTitle(e.target.value)}
            placeholder="Enter heading (e.g., HI *|FNAME|*!)"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Description</label>
          <textarea
            className="form-control"
            value={localDescription}
            onChange={(e) => setLocalDescription(e.target.value)}
            placeholder="Enter description. Use **text** for bold."
            rows={3}
          />
          <small className="text-muted">
            Tip: Use **text** for bold text.
          </small>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Coupon Instruction Text</label>
          <input
            className="form-control"
            value={localCouponInstruction}
            onChange={(e) => setLocalCouponInstruction(e.target.value)}
            placeholder="Enter instruction text for coupon code"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Coupon Code</label>
          <input
            className="form-control"
            value={localCouponCode}
            onChange={(e) => setLocalCouponCode(e.target.value)}
            placeholder="Enter coupon code"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">First Button Text</label>
          <input
            className="form-control"
            value={localButton1Text}
            onChange={(e) => setLocalButton1Text(e.target.value)}
            placeholder="Enter first button text"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">First Button Link</label>
          <input
            className="form-control"
            value={localButton1Link}
            onChange={(e) => setLocalButton1Link(e.target.value)}
            placeholder="Enter first button link URL"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Inspirational Message</label>
          <textarea
            className="form-control"
            value={localInspirationalMessage}
            onChange={(e) => setLocalInspirationalMessage(e.target.value)}
            placeholder="Enter inspirational message. Use double line breaks to separate paragraphs."
            rows={4}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Second Button Text</label>
          <input
            className="form-control"
            value={localButton2Text}
            onChange={(e) => setLocalButton2Text(e.target.value)}
            placeholder="Enter second button text"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Second Button Link</label>
          <input
            className="form-control"
            value={localButton2Link}
            onChange={(e) => setLocalButton2Link(e.target.value)}
            placeholder="Enter second button link URL"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Closing Text</label>
          <input
            className="form-control"
            value={localClosingText}
            onChange={(e) => setLocalClosingText(e.target.value)}
            placeholder="Enter closing text"
          />
        </div>
      </div>
    );
  }

  // VIEW/PREVIEW MODE
  const { primaryColor, secondaryColor, fontFamily, backgroundColor } =
    styleSettings || {};

  const inspirationalParagraphs = getParagraphs(localInspirationalMessage);

  return (
    <>
      {!previewMode && (
        <div className="email-component h1-desc-6 mb-3">
          <div className="component-controls mb-2 d-flex justify-content-end">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
          </div>
        </div>
      )}

      <table
        width="100%"
        cellPadding="0"
        cellSpacing="0"
        border="0"
        style={{
          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
          backgroundColor: backgroundColor || "#ffffff",
        }}
      >
        <tbody>
          <tr>
            <td
              align="center"
              style={{
                padding: "32px 16px",
                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
              }}
            >
              <table
                width="100%"
                cellPadding="0"
                cellSpacing="0"
                border="0"
                style={{
                  maxWidth: "600px",
                  margin: "0 auto",
                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                }}
              >
                <tbody>
                  {/* Heading (H1) */}
                  {localTitle && (
                    <tr>
                      <td
                        align="center"
                        style={{
                          paddingBottom: "16px",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <h1
                          style={{
                            fontSize: "28px",
                            fontWeight: "bold",
                            color: primaryColor || "#333333",
                            margin: "0 0 16px 0",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            textAlign: "center",
                          }}
                        >
                          {localTitle}
                        </h1>
                      </td>
                    </tr>
                  )}

                  {/* Description */}
                  {localDescription && (
                    <tr>
                      <td
                        align="center"
                        style={{
                          paddingBottom: "16px",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "16px",
                            color: primaryColor || "#333333",
                            lineHeight: "1.6",
                            margin: "0",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            textAlign: "center",
                          }}
                          dangerouslySetInnerHTML={{
                            __html: parseBoldText(localDescription),
                          }}
                        />
                      </td>
                    </tr>
                  )}

                  {/* Coupon Instruction */}
                  {localCouponInstruction && (
                    <tr>
                      <td
                        align="center"
                        style={{
                          paddingBottom: "12px",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "16px",
                            color: primaryColor || "#333333",
                            lineHeight: "1.6",
                            margin: "0",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            textAlign: "center",
                          }}
                        >
                          {localCouponInstruction}
                        </p>
                      </td>
                    </tr>
                  )}

                  {/* Coupon Code Block with Dashed Border */}
                  {localCouponCode && (
                    <tr>
                      <td
                        align="center"
                        style={{
                          padding: "24px 0",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <table
                          width="100%"
                          cellPadding="0"
                          cellSpacing="0"
                          border="0"
                          style={{
                            border: `2px dashed ${secondaryColor || "#a1894c"}`,
                            borderRadius: "8px",
                            backgroundColor: "#ffffff",
                            padding: "20px",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                          }}
                        >
                          <tbody>
                            <tr>
                              <td
                                align="center"
                                style={{
                                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "24px",
                                    fontWeight: "bold",
                                    color: primaryColor || "#333333",
                                    letterSpacing: "1px",
                                    textTransform: "uppercase",
                                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                  }}
                                >
                                  {localCouponCode}
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}

                  {/* First CTA Button */}
                  {localButton1Text && (
                    <tr>
                      <td
                        align="center"
                        style={{
                          padding: "24px 0",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <table
                          cellPadding="0"
                          cellSpacing="0"
                          border="0"
                          style={{ display: "inline-block" }}
                        >
                          <tr>
                            <td
                              style={{
                                backgroundColor: "#ffffff",
                                borderRadius: "4px",
                                border: `2px solid ${secondaryColor || "#a1894c"}`,
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              }}
                            >
                              <a
                                href={localButton1Link || "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: "block",
                                  padding: "12px 24px",
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                  color: primaryColor || "#333333",
                                  textDecoration: "none",
                                  fontFamily: fontFamily || "Arial, Helvetica, sans-serif",
                                }}
                              >
                                {localButton1Text}
                              </a>
                            </td>
                          </tr>
                        </table>
                      </td>
                    </tr>
                  )}

                  {/* Inspirational Message Section */}
                  {inspirationalParagraphs.length > 0 && (
                    <>
                      {inspirationalParagraphs.map((paragraph, index) => (
                        <tr key={index}>
                          <td
                            align="center"
                            style={{
                              paddingBottom: index < inspirationalParagraphs.length - 1 ? "16px" : "24px",
                              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            }}
                          >
                            <p
                              style={{
                                fontSize: "16px",
                                color: primaryColor || "#333333",
                                lineHeight: "1.6",
                                margin: "0",
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                textAlign: "center",
                              }}
                            >
                              {paragraph.trim()}
                            </p>
                          </td>
                        </tr>
                      ))}
                    </>
                  )}

                  {/* Second CTA Button */}
                  {localButton2Text && (
                    <tr>
                      <td
                        align="center"
                        style={{
                          padding: "24px 0",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <table
                          cellPadding="0"
                          cellSpacing="0"
                          border="0"
                          style={{ display: "inline-block" }}
                        >
                          <tr>
                            <td
                              style={{
                                backgroundColor: "#ffffff",
                                borderRadius: "4px",
                                border: `2px solid ${secondaryColor || "#a1894c"}`,
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              }}
                            >
                              <a
                                href={localButton2Link || "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: "block",
                                  padding: "12px 24px",
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                  color: primaryColor || "#333333",
                                  textDecoration: "none",
                                  fontFamily: fontFamily || "Arial, Helvetica, sans-serif",
                                }}
                              >
                                {localButton2Text}
                              </a>
                            </td>
                          </tr>
                        </table>
                      </td>
                    </tr>
                  )}

                  {/* Closing Text */}
                  {localClosingText && (
                    <tr>
                      <td
                        align="center"
                        style={{
                          paddingTop: "16px",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "16px",
                            color: primaryColor || "#333333",
                            lineHeight: "1.6",
                            margin: "0",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            textAlign: "center",
                          }}
                        >
                          {localClosingText}
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

export default H1Desc6;

